/**
 * Backend health / outage detector.
 *
 * A small event bus + axios interceptor that classifies every API
 * failure into one of three coarse states and lets the whole UI
 * subscribe once:
 *
 *   OK      — last request succeeded (any 2xx/3xx/401/403/404 counts as
 *             a healthy backend, since 401/403/404 mean the server is
 *             answering, just refusing this specific request).
 *   OFFLINE — `navigator.onLine === false` OR the browser reports the
 *             request was blocked before it ever left (ERR_NETWORK
 *             with `navigator.onLine === false`).
 *   OUTAGE  — backend appears unreachable: repeated 5xx, ECONNABORTED
 *             timeout, or ERR_NETWORK while `navigator.onLine === true`.
 *
 * Gated behind BACKEND_OUTAGE_UI_ENABLED. When the flag is false the
 * installer is a no-op and the whole module tree is DCE-eligible.
 *
 * Never surface raw HTTP text to end users. Consumers should read the
 * state and render an outage-scoped copy string from `outageMessages.js`.
 *
 * See:
 *   • /app/docs/PRODUCTION_INCIDENTS.md INC-001
 *   • /app/docs/BACKEND_DEPENDENCY_MATRIX.md
 *   • /app/docs/GRACEFUL_DEGRADATION_AUDIT.md
 *   • /app/memory/FOUNDER_DECISIONS.md D-034 / D-035
 */

import api from "./api";
import { BACKEND_OUTAGE_UI_ENABLED } from "./featureFlags";

export const HEALTH_OK = "OK";
export const HEALTH_OFFLINE = "OFFLINE";
export const HEALTH_OUTAGE = "OUTAGE";

// Consecutive-failure threshold before we flip OK → OUTAGE. Prevents
// a single flaky request from panicking the UI. A background ping
// confirms recovery on the way back.
const OUTAGE_STREAK_THRESHOLD = 2;

// Background probe cadence while degraded — cheap unauth GET.
const RECOVERY_POLL_MS = 15000;

// Statuses that mean "the backend answered, this specific call was
// refused for a valid reason". These do NOT count as an outage.
const HEALTHY_REFUSAL_STATUSES = new Set([400, 401, 403, 404, 405, 409, 422, 429]);

const state = {
  current: HEALTH_OK,
  failStreak: 0,
  lastChangeAt: 0,
  timer: null,
  listeners: new Set(),
  installed: false,
};

function _emit() {
  for (const fn of state.listeners) {
    try {
      fn(state.current);
    } catch {
      /* subscribers must not throw back */
    }
  }
}

function _setState(next) {
  if (state.current === next) return;
  state.current = next;
  state.lastChangeAt = Date.now();
  _emit();
}

export function getBackendHealth() {
  return state.current;
}

export function subscribeBackendHealth(fn) {
  state.listeners.add(fn);
  // Fire immediately so consumers hydrate.
  try {
    fn(state.current);
  } catch {
    /* ignore */
  }
  return () => {
    state.listeners.delete(fn);
  };
}

function _isOffline() {
  try {
    return typeof navigator !== "undefined" && navigator.onLine === false;
  } catch {
    return false;
  }
}

function _classifyError(error) {
  // navigator.onLine takes precedence — user's own connectivity.
  if (_isOffline()) return HEALTH_OFFLINE;
  const status = error?.response?.status;
  if (typeof status === "number") {
    if (HEALTHY_REFUSAL_STATUSES.has(status)) return HEALTH_OK;
    if (status >= 500 && status <= 599) return HEALTH_OUTAGE;
    // 3xx / other odd 4xx — treat as OK (server answered).
    return HEALTH_OK;
  }
  // Network-level failure: axios sets code ECONNABORTED (timeout) or
  // ERR_NETWORK (no response at all — this is what "HTTP (no response)"
  // looks like from the wire).
  const code = error?.code;
  if (code === "ECONNABORTED" || code === "ERR_NETWORK" || code === "ERR_CANCELED") {
    return HEALTH_OUTAGE;
  }
  // Unknown — don't panic.
  return HEALTH_OK;
}

async function _pingHealth() {
  try {
    // No credentials, small payload, uses the same base URL and CORS
    // config as every other client call → if this succeeds, the whole
    // /api surface is reachable.
    await api.get("/health", { timeout: 6000, __skipHealthTracking: true });
    state.failStreak = 0;
    if (state.current !== HEALTH_OK) _setState(HEALTH_OK);
    // Stop polling once healthy again.
    if (state.timer) {
      clearInterval(state.timer);
      state.timer = null;
    }
  } catch (err) {
    const c = _classifyError(err);
    if (c !== HEALTH_OK) _setState(c);
  }
}

function _startRecoveryPollIfNeeded() {
  if (state.timer) return;
  state.timer = setInterval(_pingHealth, RECOVERY_POLL_MS);
}

/**
 * Install the axios response interceptor and browser online/offline
 * listeners. Safe to call multiple times — the second call is a no-op.
 * Automatically no-ops when BACKEND_OUTAGE_UI_ENABLED is false.
 */
export function installBackendHealthDetector() {
  if (!BACKEND_OUTAGE_UI_ENABLED) return;
  if (state.installed) return;
  state.installed = true;

  api.interceptors.response.use(
    (response) => {
      // Any successful response resets the streak and flips us back OK.
      if (!response?.config?.__skipHealthTracking) {
        state.failStreak = 0;
        if (state.current !== HEALTH_OK) _setState(HEALTH_OK);
      }
      return response;
    },
    (error) => {
      if (error?.config?.__skipHealthTracking) return Promise.reject(error);
      const classification = _classifyError(error);
      if (classification === HEALTH_OFFLINE) {
        _setState(HEALTH_OFFLINE);
      } else if (classification === HEALTH_OUTAGE) {
        state.failStreak += 1;
        if (state.failStreak >= OUTAGE_STREAK_THRESHOLD) {
          _setState(HEALTH_OUTAGE);
          _startRecoveryPollIfNeeded();
        }
      } else {
        // Healthy refusal — reset streak but leave current state alone.
        state.failStreak = 0;
      }
      return Promise.reject(error);
    },
  );

  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("online", () => {
      // Coming back online — force a fresh probe rather than assuming.
      _pingHealth();
    });
    window.addEventListener("offline", () => {
      _setState(HEALTH_OFFLINE);
    });
  }
}
